import { useEffect, useRef } from "react";

/** Keep keyboard focus in the open surface and return it to its trigger. */
export function useModal(open: boolean, onClose: () => void) {
  const ref = useRef<HTMLDivElement>(null);
  const close = useRef(onClose);
  close.current = onClose;
  useEffect(() => {
    if (!open || !ref.current) return;
    const surface = ref.current;
    const previous = document.activeElement as HTMLElement | null;
    const focusable = () => Array.from(surface.querySelectorAll<HTMLElement>(
      'a[href], button:not(:disabled), input:not(:disabled), textarea:not(:disabled), select:not(:disabled), [tabindex="0"]',
    )).filter((element) => element.getClientRects().length > 0);
    (focusable()[0] ?? surface).focus();
    const keydown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        event.preventDefault();
        close.current();
      }
      if (event.key !== "Tab") return;
      const elements = focusable();
      const first = elements[0] ?? surface;
      const last = elements.at(-1) ?? surface;
      if (event.shiftKey && (document.activeElement === first || document.activeElement === surface)) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && (document.activeElement === last || document.activeElement === surface)) {
        event.preventDefault();
        first.focus();
      }
    };
    surface.addEventListener("keydown", keydown);
    return () => {
      surface.removeEventListener("keydown", keydown);
      if (previous?.isConnected) previous.focus();
    };
  }, [open]);
  return ref;
}
